import { Component, DestroyRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Color, Size, Style } from 'src/app/components/company/models/new/style';
import { Selectable } from 'src/app/shared/types/selectable.type';
import * as _ from 'lodash';
import { OrderQueries } from 'src/app/shared/types/get-query';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'bt-manage-colors-sizes-modal',
	standalone: false,
	templateUrl: './manage-colors-sizes-modal.component.html',
	styleUrl: './manage-colors-sizes-modal.component.scss'
})
export class ManageColorsSizesModalComponent {
	readonly data = inject<{ style: Style; singleColorSelection?: boolean; isFake?: boolean }>(MAT_DIALOG_DATA);
	readonly dialogRef = inject(MatDialogRef<ManageColorsSizesModalComponent>);
	readonly #graphQLService = inject(GraphQLService);
	readonly #destroyRef = inject(DestroyRef);
	groupedWarehouseArticles: { color: Selectable<Color>; sizesAndQuantity: Selectable<{ size: Size; availableQty: number }>[] }[] = [];
	colorDraftImages: Record<number, string> = {};

	constructor() {
		if (!this.data?.style) {
			return;
		}

		if (this.data.isFake) {
			this.buildFromSchemas();
		} else if (this.data.style.colors && this.data.style.warehouseArticles) {
			this.buildFromWarehouseArticles();
		}

		this.getColorDraftImages();
	}

	private buildFromWarehouseArticles(): void {
		this.groupedWarehouseArticles = this.data.style.colors!.map((color: Color) => {
			const sizesAndQuantity: Selectable<{ size: Size; availableQty: number }>[] = this.data.style.warehouseArticles!
				.filter((wa) => wa.color && wa.color.id == color.id && wa.size)
				.map((wa) => ({
					size: new Size({ id: wa.size!.id, name: wa.size!.name }),
					availableQty: wa.availableQty ?? 0,
					selected: false
				}));

			sizesAndQuantity.forEach((s) => {
				if (color.size && s.size && color.size.id == s.size.id) {
					s.selected = true;
				}
			});

			const selectableColor: Selectable<Color> = { ...color, selected: false };

			this.data.style.colors!.forEach((c) => {
				if (c.id == color.id) {
					selectableColor.selected = true;
				}
			});

			return { color: selectableColor, sizesAndQuantity };
		});

		if (!this.data.singleColorSelection) {
			const groupedWarehouseArticlesNotSelectedWithAllSizes = this.data.style.warehouseArticles!.reduce(
				(acc: { color: Selectable<Color>; sizesAndQuantity: Selectable<{ size: Size; availableQty: number }>[] }[], wa) => {
					if (wa.color && !acc.some((a) => a.color && a.color.id == wa.color!.id)) {
						acc.push({
							color: { ...wa.color, selected: false },
							sizesAndQuantity: this.data.style.warehouseArticles!
								.filter((w) => w.color && w.color.id == wa.color!.id && w.size)
								.map((w) => ({
									size: new Size({ id: w.size!.id, name: w.size!.name }),
									availableQty: w.availableQty ?? 0,
									selected: false
								}))
						});
					}
					return acc;
				},
				[]
			);

			groupedWarehouseArticlesNotSelectedWithAllSizes.forEach((wa) => {
				if (!this.groupedWarehouseArticles.some((g) => g.color.id == wa.color.id)) {
					this.groupedWarehouseArticles.push(wa);
				}
			});
		}

		this.groupedWarehouseArticles.sort((a, b) => (a.color.name || '').localeCompare(b.color.name || ''));
	}

	private buildFromSchemas(): void {
		const schemas = this.data.style.schemas ?? [];
		const schemaSizes = _.uniqBy(
			_.flatten(schemas.map((schema) => schema.schemaSizes ?? [])).filter((size) => size.sizeId != null),
			'sizeId'
		);

		const buildSizesAndQuantity = (selectedSizeId?: number) =>
			schemaSizes.map((schemaSize) => ({
				size: new Size({ id: schemaSize.sizeId, name: schemaSize.sizeCode }),
				availableQty: 1,
				selected: selectedSizeId != null && schemaSize.sizeId == selectedSizeId
			}));

		const selectedColors = this.data.style.colors ?? [];

		this.groupedWarehouseArticles = selectedColors.map((color) => ({
			color: { ...color, selected: true },
			sizesAndQuantity: buildSizesAndQuantity(color.size?.id)
		}));

		if (!this.data.singleColorSelection) {
			const schemaColors = _.uniqBy(
				_.flatten(schemas.map((schema) => schema.schemaTeamColors ?? [])).filter((stc) => stc.colorId != null),
				'colorId'
			);

			schemaColors.forEach((stc) => {
				if (this.groupedWarehouseArticles.some((g) => g.color.id == stc.colorId)) {
					return;
				}

				this.groupedWarehouseArticles.push({
					color: {
						id: stc.colorId,
						code: stc.colorCode,
						selected: false
					},
					sizesAndQuantity: buildSizesAndQuantity()
				});
			});
		}

		this.groupedWarehouseArticles.sort((a, b) => (a.color.name || a.color.code || '').localeCompare(b.color.name || b.color.code || ''));
	}

	canSelectSize(size: Selectable<{ size: Size; availableQty: number }>): boolean {
		return !!this.data.isFake || (size.availableQty ?? 0) > 0;
	}

	canSelectColor(color: { sizesAndQuantity: Selectable<{ size: Size; availableQty: number }>[] }): boolean {
		return color.sizesAndQuantity.some((s) => this.canSelectSize(s));
	}

	hasSelectableColors(): boolean {
		return this.groupedWarehouseArticles.some((c) => this.canSelectColor(c));
	}

	selectAllColors(): void {
		this.groupedWarehouseArticles.forEach((c) => {
			if (!this.canSelectColor(c)) {
				return;
			}

			// Solo i colori: le taglie le sceglie l'utente.
			c.color.selected = true;
		});
	}

	deselectAllColors(): void {
		this.groupedWarehouseArticles.forEach((c) => {
			c.color.selected = false;
			c.sizesAndQuantity.forEach((s) => (s.selected = false));
		});
	}

	toggleColor(color: any) {
		color.color.selected = !color.color.selected;
		if (!color.color.selected) {
			color.sizesAndQuantity.forEach((s: any) => {
				s.selected = false;
			});
		}
	}

	disableApply() {
		const selectedColors = this.groupedWarehouseArticles.filter((c) => c.color.selected);
		if (selectedColors.length == 0) {
			return true;
		}
		for (const color of selectedColors) {
			const selectedSizes = color.sizesAndQuantity.filter((s) => s.selected);
			if (selectedSizes.length == 0) {
				return true;
			}
		}
		return false;
	}

	selectSize(color: Color, size: Selectable<{ size: Size; availableQty: number }>) {
		this.groupedWarehouseArticles.map((c) => {
			if (c.color.id == color.id) {
				c.sizesAndQuantity.forEach((s) => {
					s.selected = false;
				});
			}
		});
		size.selected = !size.selected;
	}

	close() {
		this.dialogRef.close(null);
	}

	apply() {
		this.dialogRef.close(this.groupedWarehouseArticles);
	}

	private buildModelColorsForDraftImages(): { modelId: number; colorId: number }[] {
		const modelId = this.data.style.modelId;
		if (modelId == null) {
			return [];
		}

		// isFake: tutte le combinazioni colore già in UI (schemas)
		// altrimenti: solo colori con giacenza (almeno una taglia con availableQty > 0)
		const colors = this.data.isFake
			? this.groupedWarehouseArticles
			: this.groupedWarehouseArticles.filter((c) => this.canSelectColor(c));

		return _.uniqBy(
			colors.filter((c) => c.color?.id != null).map((c) => ({ modelId, colorId: c.color.id! })),
			'colorId'
		);
	}

	getColorDraftImages(): void {
		const modelColors = this.buildModelColorsForDraftImages();
		if (!modelColors.length) {
			return;
		}

		this.#graphQLService
			.getData<any>(OrderQueries.GET_COLOR_DRAFT_IMAGES, { modelColors })
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: any) => {
					const drafts = res.data?.imageColorDrafts ?? [];
					this.colorDraftImages = drafts.reduce(
						(acc: Record<number, string>, draft: { colorId?: number; imgPath?: string }) => {
							if (draft.colorId != null && draft.imgPath) {
								acc[draft.colorId] = draft.imgPath;
							}
							return acc;
						},
						{}
					);
				},
				error: (err) => {
					console.error(err);
				}
			});
	}

	getColorDraftImage(colorId?: number): string | undefined {
		if (colorId == null) {
			return undefined;
		}
		return this.colorDraftImages[colorId];
	}
}
